import { type Action, type ThunkDispatch } from '@reduxjs/toolkit';
import { type CharacterSet } from '@tari-labs/emojis-wasm-default';

import { apiFactory } from '@app/client';
import {
    type CartResponse,
    type CheckoutResponseV2,
    type EmojiCopy,
    type Order,
    type OrderItems,
    type ResourceId,
} from '@app/client/common/structs';
import { CartStatus } from '@app/client/common/structs/orders/cart';
import { Admin, Cart } from '@app/client/resources';

import { type RootState } from '../reducers';
import {
    ADD_EMOJI,
    ADD_PRE_EMOJIS,
    COPY_EMOJI,
    type currentIndexInteractionResponse,
    type Emoji,
    type EmojiListStatus,
    GET_CART_ITEMS,
    INIT_SELECT_INDEX,
    NEXT_SEARCH_EMOJI,
    PASTE_EMOJI,
    PREV_SEARCH_EMOJI,
    REMOVE_EMOJI,
    SET_CART_ITEMS,
    SET_DELETE_CART_ITEM_REQUEST_IN_PROGRESS,
    SET_EMOJI_LIST,
    SET_INPUT_FOCUSED,
    SET_INPUT_VALUE,
    SET_ORDER_ITEMS,
    SET_RHYTHM_SCORE,
    SET_RHYTHM_SCORE_COPY,
    SET_SEARCH_EMOJI,
    SET_SECRET_KEY,
    SET_WASM_FILTER,
    UPDATE_EMOJIS,
    UPDATE_SEARCH,
    UPDATE_STATUS,
} from '../types/picker';

export const addEmoji = (emoji: Emoji) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
    dispatch({
        type: ADD_EMOJI,
        payload: emoji,
    });
};

export const addPreEmojis = (emojis: Emoji[]) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
    dispatch({
        type: ADD_PRE_EMOJIS,
        payload: emojis,
    });
};

export const removeEmoji = (removalIndex: number) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
    dispatch({
        type: REMOVE_EMOJI,
        payload: removalIndex,
    });
};

export const pasteEmoji = () => async (dispatch: ThunkDispatch<RootState, void, Action>) => {
    const clipboard = await navigator.clipboard.readText();
    dispatch({
        type: PASTE_EMOJI,
        payload: clipboard,
    });
};

export const copyEmoji = () => (dispatch: ThunkDispatch<RootState, void, Action>) => {
    dispatch({
        type: COPY_EMOJI,
    });
};

export const setSearchEmoji =
    (newIndex: currentIndexInteractionResponse) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
        dispatch({
            type: SET_SEARCH_EMOJI,
            payload: newIndex,
        });
    };

export const prevSearchEmoji = () => (dispatch: ThunkDispatch<RootState, void, Action>) => {
    dispatch({
        type: PREV_SEARCH_EMOJI,
    });
};

export const nextSearchEmoji = () => (dispatch: ThunkDispatch<RootState, void, Action>) => {
    dispatch({
        type: NEXT_SEARCH_EMOJI,
    });
};

export const initSelectIndex = () => (dispatch: ThunkDispatch<RootState, void, Action>) => {
    dispatch({
        type: INIT_SELECT_INDEX,
    });
};

export const setInputValue = (inputValue: string) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
    dispatch({
        type: SET_INPUT_VALUE,
        payload: inputValue,
    });
};

export const setInputFocus = (isFocused: boolean) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
    dispatch({
        type: SET_INPUT_FOCUSED,
        payload: isFocused,
    });
};

export const updateSearch = (inputValue: string) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
    dispatch({
        type: UPDATE_SEARCH,
        payload: inputValue,
    });
};

export const updateStatus = (status: EmojiListStatus) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
    dispatch({
        type: UPDATE_STATUS,
        payload: status,
    });
};

export const updateEmojis = (emojis: Array<Emoji>) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
    dispatch({
        type: UPDATE_EMOJIS,
        payload: emojis,
    });
};

export const setEmojiFilter = (filter: CharacterSet) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
    dispatch({
        type: SET_WASM_FILTER,
        payload: filter,
    });
};

export const setEmojiList = (emojis: Array<Emoji>) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
    dispatch({
        type: SET_EMOJI_LIST,
        payload: emojis,
    });
};

export const setRhythmScore = (score: number) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
    dispatch({
        type: SET_RHYTHM_SCORE,
        payload: score,
    });
};

export const setRhythmScoreCopy = (copy: EmojiCopy) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
    dispatch({
        type: SET_RHYTHM_SCORE_COPY,
        payload: copy,
    });
};

export const setOrderItems = (orderItems: OrderItems[]) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
    dispatch({
        type: SET_ORDER_ITEMS,
        payload: orderItems,
    });
};

export const setDeleteCartItemRequestInProgress =
    (requestInProgress: boolean) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
        dispatch({
            type: SET_DELETE_CART_ITEM_REQUEST_IN_PROGRESS,
            payload: requestInProgress,
        });
    };

export const getManualOrderItems = (id: ResourceId) => async (dispatch: ThunkDispatch<RootState, void, Action>) => {
    await apiFactory()
        .call(Admin.Orders.getOrder, { id })
        .then((r) => {
            dispatch(setReduxCartItems(r.ok ? r.value : undefined));
        });
};

export const getCartItems = () => async (dispatch: ThunkDispatch<RootState, void, Action>) => {
    return await apiFactory()
        .call(Cart.getCart, {})
        .then((r) => {
            dispatch(setReduxCartItems(r.ok ? r.value : undefined));
            return r;
        });
};

export const setReduxCartItems =
    (cart?: CartResponse | Order) => async (dispatch: ThunkDispatch<RootState, void, Action>) => {
        let payload;
        if (cart) {
            const orderId = cart.order_items[0]?.order_id;
            payload = {
                orderItems: cart.order_items,
                savedSecretKey: cart.payment_method_data?.client_secret,
                secondsRemaining: cart.seconds_until_expiry,
                cartStatus: cart.status,
                orderId,
                initial: false,
            };
        } else {
            payload = {
                orderItems: [],
                orderId: '',
                savedSecretKey: '',
                secondsRemaining: null,
                cartStatus: CartStatus.Draft,
            };
        }
        dispatch({
            type: GET_CART_ITEMS,
            payload,
        });
    };

export const setSecretKey = (secretKey: string) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
    dispatch({
        type: SET_SECRET_KEY,
        payload: secretKey,
    });
};

export const setCartItems = (cartItems: CheckoutResponseV2) => (dispatch: ThunkDispatch<RootState, void, Action>) => {
    dispatch({
        type: SET_CART_ITEMS,
        payload: cartItems,
    });
};
